import { ApiError } from '@/shared/data/client';
import type { StoredSession } from './sessionStorage';
import type { AuthSession } from './types';

export type RefreshOutcome = {
  session: AuthSession | null;
  didRefresh: boolean;
};

export const mergeSessionFallbacks = (
  refreshed: AuthSession,
  fallback: AuthSession | null,
): AuthSession => {
  if (!fallback) return refreshed;
  if (fallback.userId !== refreshed.userId) {
    return refreshed;
  }
  return {
    ...refreshed,
    orgId: refreshed.orgId ?? fallback.orgId,
    orgName: refreshed.orgName ?? fallback.orgName,
    lastOrganisationId: refreshed.lastOrganisationId ?? fallback.lastOrganisationId,
    lastProjectId: refreshed.lastProjectId ?? fallback.lastProjectId,
    onboardingCompleted: refreshed.onboardingCompleted ?? fallback.onboardingCompleted,
    emailVerified: refreshed.emailVerified ?? fallback.emailVerified,
    roles: refreshed.roles ?? fallback.roles,
    superuserEmailMatched: refreshed.superuserEmailMatched ?? fallback.superuserEmailMatched,
  };
};

export const normalizeStoredSession = (stored: StoredSession): AuthSession => ({
  ...stored,
  orgId: stored.orgId ?? undefined,
  orgName: stored.orgName ?? undefined,
});

export const wait = async (ms: number): Promise<void> => {
  await new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
};

export const resolveTokenFromClerkClientSessions = async (
  client: unknown,
): Promise<string | null> => {
  if (!client || typeof client !== 'object') {
    return null;
  }
  const sessionsRaw = (client as { sessions?: unknown }).sessions;
  if (!Array.isArray(sessionsRaw) || sessionsRaw.length === 0) {
    return null;
  }
  const lastActiveSessionIdRaw = (client as { lastActiveSessionId?: unknown }).lastActiveSessionId;
  const lastActiveSessionId =
    typeof lastActiveSessionIdRaw === 'string' ? lastActiveSessionIdRaw : null;
  const preferredSession = sessionsRaw.find((candidate) => {
    if (!candidate || typeof candidate !== 'object') {
      return false;
    }
    const candidateId = (candidate as { id?: unknown }).id;
    return typeof candidateId === 'string' && candidateId === lastActiveSessionId;
  });
  const sessionCandidate = preferredSession ?? sessionsRaw[0];
  if (!sessionCandidate || typeof sessionCandidate !== 'object') {
    return null;
  }
  const getTokenCandidate = (sessionCandidate as { getToken?: unknown }).getToken;
  if (typeof getTokenCandidate !== 'function') {
    return null;
  }
  try {
    const token = await (getTokenCandidate as () => Promise<string | null | undefined>)();
    return typeof token === 'string' && token.length > 0 ? token : null;
  } catch {
    return null;
  }
};

export const bootstrapTimeoutMs = 20_000;
export const bootstrapRetryIntervalMs = 400;
const storedSessionBootstrapTtlMs = 2 * 60_000;
const minimumStoredSessionRemainingMs = 30_000;

export const isMissingUserIdentityError = (error: unknown): boolean => {
  if (!(error instanceof ApiError)) return false;
  const message = error.message.toLowerCase();
  return error.code === 'AUTH_FAILED' && message.includes('missing user identity');
};

export const isUserNotFoundError = (error: unknown): boolean => {
  if (!(error instanceof ApiError)) return false;
  const message = error.message.toLowerCase();
  if (error.code === 'AUTH_FAILED' && message.includes('user not found')) {
    return true;
  }
  return error.status === 404 && error.code === 'NOT_FOUND' && message.includes('user not found');
};

export const isIdentityMismatchError = (error: unknown): boolean => {
  if (!(error instanceof ApiError)) return false;
  if (error.code !== 'AUTH_FAILED') return false;
  return error.message.toLowerCase().includes('identity mismatch');
};

export const shouldPreserveSessionOnSignedOutSignal = (input: {
  hasSignedInSignal: boolean;
  currentSession: AuthSession | null;
}): boolean => {
  if (input.hasSignedInSignal) return false;
  return input.currentSession !== null;
};

export const shouldPreserveSessionOnRefreshMiss = (input: {
  refreshedSession: AuthSession | null;
  currentSession: AuthSession | null;
}): boolean => {
  if (input.refreshedSession) return false;
  return input.currentSession !== null;
};

export const shouldTrustStoredSessionBootstrap = (session: AuthSession): boolean => {
  const bootstrappedAt = session.bootstrappedAt;
  if (typeof bootstrappedAt !== 'number') {
    return false;
  }
  if (Date.now() - bootstrappedAt > storedSessionBootstrapTtlMs) {
    return false;
  }
  const expiresAt = session.expiresAt;
  if (typeof expiresAt === 'number' && expiresAt - Date.now() <= minimumStoredSessionRemainingMs) {
    return false;
  }
  return true;
};
